import {BaseElement, BaseElementAsync, BASIS, isEltInitableAsyncPending} from "/@back@/commons/basis.js";
import {Tab, Tabs} from "/@back@/commons/widgets/tabs.js";
import {ItemXmlEd, OItemXmlEdInit} from "/@back@/wsp/views/item/itemXmlEd.js";
import {IInfo, IInfoBroker, IInfoConsumer, InfoBrokerBasic} from "/@lib@/commons/infos.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {SEC} from "/@lib@/commons/security.js";
import {IView, IViewApi, IViewsContainer, OBaseAreaViewInit, OViewVisitOptions, VIEWS} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {JSrcFields, SRC, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {IWspUiEnv, JWspUriChangeMsg, WSP, Wsp} from "/@lib@/wsp/wsp.js";
import {EItemTypeFamily, IDatasItemTab, IDatasSrcView, IDatasTaskItemType, IDatasTaskView, IItemTypeUiEnv, ItemType, ItemTypeTask, loadFreeLib} from "/@lib@/wsp/wspMetaUi.js";
import {Area, AreaAsync, IArea, isArea, SimpleTagArea} from "/@lib@/commons/areas.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {InfoFocusItem, InfoFocusNodeInItem, IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {IXAddr, XA} from "/@lib@/commons/xml/xAddr.js";
import {WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {IRibbonAreaContext, RibbonBase} from "/@back@/wsp/widgets/ribbon/ribbon.js";
//Force le chargement des ribons.
import "/@back@/wsp/widgets/ribbon/ribbon.js";
import {EventMgr} from "/@lib@/commons/events.js";
import {ACTION, Action, ActionMenu, IAction} from "/@lib@/commons/actions.js";
import {ActionBtn, Button, OActionBtnInit, OButtonInit} from "/@back@/commons/widgets/buttons.js";
import {ExportScar, MoveToSrc, PasteContent, ReloadSrc, RenameSrc} from "/@back@/wsp/actions/srcActions.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {ItemMsgSs} from "/@back@/wsp/views/item/itemMsgSs.js";
import {Resizer} from "/@back@/commons/widgets/resizer.js";
import {JTaskStates, TASK} from "/@lib@/wsp/lcTask.js";
import {isApp} from "/@back@/core/appFrame.js";
import {InfoChangeTask, InfoReqStateTask} from "/@back@/wsp/views/task/taskFields.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {EWspChangesEvts, IWspXmlHouse, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {Desk} from "/@lib@/commons/desk.js";
import {IEndPoint, IO} from "/@lib@/commons/io/io.js";
import "/@back@/core/zones/webZones.js";
import {LANG} from "/@lib@/commons/lang.js";
import {ExportRes} from "/@back@/wsp/actions/itemActions.js";

/**
 * Contexte pour l'initialisation d'une view Main d'une source.
 */
export interface ISrcMainCtx extends IRegPointer<IItemTypeUiEnv> {

	shortDesc: JSrcFields

	/** Optimisation : à renseigner si disponible et que l'appelant le sait à jour. */
	longDesc?: JSrcFields

	/** Code spécial de la mainView à utiliser. */
	mainViewCode?: string

	/** Customisation du registre associé à cette mainView. */
	//configReg?: (reg: IReg<IItemUiEnv>) => void
}


/** Un environnement d'item est issu d'un env d'itemType, avec le securityCtx dédié, une place enregistrée et le longDesc de l'item. */
export type IItemUiEnv = IItemTypeUiEnv & {
	srcView?: IView & IShortDescCtx
	longDesc?: JSrcFields
	longDescChange?: EventMgr<(ldNew: JSrcFields, ldOld: JSrcFields) => void>
}

export function isItemUiEnv(env: any): env is IItemUiEnv {return env && env.longDesc != null}

export type ITaskUiEnv = IItemUiEnv & {
	srcView: TaskMain
	itemType: ItemTypeTask
	taskEditBroker?: IInfoBroker
	/** Contenu de la task */
	taskContent?: Document
	/** Etat des champs de la tâche à son chargement (création de task avec pre-init ou reload de page). */
	initStates?: JTaskStates
}

/**
 * Composant de base pour les mainViews des items.
 */
export interface SrcMain {
	initialize(init: OSrcMainInit): this;
}

export interface OSrcMainInit extends OBaseAreaViewInit<ISrcMainCtx> {}

export abstract class SrcMain extends BaseElementAsync implements IInfoConsumer, IView, IViewsContainer, IShortDescCtx {

	reg: IReg<IItemUiEnv>;
	wsp: Wsp;
	infoBroker: IInfoBroker;

	/** views affichés dans cette page principale (hors tabs) */
	subViews?: IView[];
	/** Onglets de la view. */
	tabs?: Tabs<SrcMain>;

	async _initialize(init: OSrcMainInit) {
		this.wsp = init.areaContext.reg.env.wsp;
		this.infoBroker = init.areaContext.reg.env.infoBroker;
		if (this.infoBroker) this.infoBroker.addConsumer(this); //on s'abonne de suite pour ne pas louper des demandes de type serchText/searchItemRef, etc.
		this.reg = REG.createSubReg(init.areaContext.reg);
		this.reg.env.place = this.wsp.newPlaceWsp();

		let longDesc = init.areaContext.longDesc;
		if (!longDesc) {
			if (SRC.isNewSrcUri(init.areaContext.shortDesc.srcUri)) {
				longDesc = init.areaContext.shortDesc;
				longDesc.srcRoles = this.reg.env.securityCtx.srcRoles; //objet en création, on récupère les roles de l'atelier.
			} else {
				longDesc = await this.wsp.fetchLongDesc(SRC.srcRef(init.areaContext.shortDesc));
			}
		}
		//console.log("longDesc:::", longDesc);
		this.reg.env.longDesc = longDesc;
		this.shortDescs[0] = longDesc;
		this.reg.env.srcView = this;
		this.reg.env.longDescChange = new EventMgr();
		this.reg.env.securityCtx = SEC.createSub(this.reg.env.securityCtx, longDesc.srcRoles, longDesc.srcRi, longDesc);

		this.reg.env.itemType.configRegForMainView(init.areaContext, this.reg);
		//if (init.areaContext.configReg) init.areaContext.configReg(this.reg);

		this.reg.env.place.eventsMgr.on("wspUriChange", this.onWspUriChange.bind(this));

		if (ITEM.isExtItem(longDesc.srcUri)) {
			//On écoute les modifs de cet atelier étranger public.
			this.reg.env.place.getWsp(WSP.extractWspCdFromWspRef(longDesc.itFullUriInOwnerWsp));
		}

		return this._buildContent(init);
	}

	protected async _buildContent(init: OSrcMainInit): Promise<void> {
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:page", this.shadowRoot);
		this.reg.installSkin("wsp-itemmain", this.shadowRoot);
		this._initAndInstallSkin(this.localName, init);
		const itemType = this.reg.env.itemType;
		const mainView = itemType.getDataSrcMainView(init.areaContext);
		const ribRoot = this.shadowRoot.appendChild<HTMLElement>(<header id="ribbons"/>);
		const ribClasses = this.reg.getList<typeof RibbonBase | IArea<IRibbonAreaContext>>("ribbon:item");
		if (ribClasses) {
			const ribbons: RibbonBase[] = [];
			//let resizable = 0;
			for (let i = 0; i < ribClasses.length; i++) {
				//if (i > 0) ribRoot.appendChild(<c-resizer class="ribSep" c-orient="row"/>);

				let rib: RibbonBase;
				if (isArea(ribClasses[i])) {
					const areaCtx: IRibbonAreaContext = {
						reg: this.reg
					};
					if ((ribClasses[i] as IArea).isAvailable(areaCtx)) {
						if ((ribClasses[i] as IArea).needAsync(areaCtx)) {
							rib = ribRoot.appendChild(await (ribClasses[i] as AreaAsync).loadBody(areaCtx, init.lastDatas)) as RibbonBase;
						} else {
							rib = ribRoot.appendChild((ribClasses[i] as IArea).buildBody(areaCtx, init.lastDatas)) as RibbonBase;
						}
					}
				} else
					rib = ribRoot.appendChild(new (ribClasses[i] as typeof RibbonBase)());
				if (rib) {
					ribbons.push(rib);
					//if (rib.hasAttribute("c-resizable")) resizable++;
				}
			}
			// //Au moins 2 ribons resizables pour activer les resizers.
			// if (resizable <= 1) ribRoot.querySelectorAll('c-resizer').forEach((r: Resizer) => r.disabled = true);
			this.subViews = ribbons;
		}

		const converters = itemType.getConvertersForMainView(init.areaContext);
		if (converters) this.reg.addToList("actions:item:burger", "converters", 1, new ItemMainConverters(converters), 100);

		let burgerActions = this.reg.mergeLists<IAction<SrcMain>>("actions:item:burger", "actions:wsp:shortDesc");
		if (burgerActions) {
			burgerActions = ACTION.injectSepByGroup<SrcMain>(burgerActions, "", this);
			ribRoot.insertBefore(<ActionBtn ui-context="bar" î={{
				reg: this.reg,
				action: new ActionMenu<SrcMain>().setActions(burgerActions).setIcon("/@skin@/commons/icons/actions.svg").setLabel("Actions générales sur l'item"),
				actionContext: this
			} as OActionBtnInit<SrcMain>}/> as ActionBtn<SrcMain>, ribRoot.firstChild);
		}

		if (mainView && mainView.showSsErrors) {
			this.shadowRoot.appendChild<HTMLElement>(new ItemMsgSs().initialize({
				reg: this.reg,
				errors: mainView.showSsErrors.errors
			}));
		}

		let tabs: Area<SrcMain>[];
		const histView = this.wsp.hasFeature("history")
			&& LANG.in(itemType.getFamily(), EItemTypeFamily.xml, EItemTypeFamily.res, EItemTypeFamily.undef, EItemTypeFamily.other)
			&& this.reg.getPref("wsp.item.histotryView", true);
		if (mainView?.subTabs?.length > 0 || histView) {
			const datasTabs = mainView?.subTabs; //.filter((tab) => tab.showPerms ? this.reg.hasPerm(tab.showPerms) : true); NON, perms peuvent évoluer, donc affichage en mode tabs
			tabs = [new ItemHomeArea(init).setLabel(mainView?.mainTabLabel || "Édition").setIcon(mainView?.mainTabIcon || "/@skin@/wsp/views/itemMain/penInk.svg")];
			if (datasTabs) for (const d of datasTabs) tabs.push(new ItemSubTabArea(d, this));
			if (histView) {
				tabs.push(new ItemSubTabArea({
					code: "~hist",
					label: "Historique",
					description: "Liste les derniers changements de cet item",
					icon: "/@skin@/wsp/views/itemMain/history.svg",
					view: {
						tag: "wsp-item-histo"
					}
				}, this));
			}
		}
		if (tabs) {
			this.tabs = new Tabs<SrcMain>();
			this.tabs.classList.add("noBorder");
			this.shadowRoot.appendChild(this.tabs.initialize({
				reg: this.reg,
				areasContext: this,
				areas: tabs,
				lastDatasKey: "vtabs",
				lastDatas: init.lastDatas ? init.lastDatas["vtabs"] : null,
				skinOver: "itemMain/c-tabs",
				tabSkinOver: "itemMain/c-tab"
			}));
			await this.tabs.initializedAsync;
			//après init, on injecte l'enregistrement des houses avant tout changement de tab (pour Màj avant init des dynGen en particulier)
			this.tabs.selectTab = async function (this: Tabs<SrcMain>, tab: Tab<SrcMain>, lastDatas?: JLastDatas): Promise<boolean> {
				await REG.findReg<IWspUiEnv>(this).env.universe.wspServer.wspsLive.saveAllHouses();
				return Tabs.prototype.selectTab.call(this, tab, lastDatas);
			};
			if (!this.subViews) this.subViews = [this.tabs];
			else this.subViews.push(this.tabs);
		} else {
			const body = this.shadowRoot.appendChild(<c-views-ctn id="noTabs"/>);
			if (!this.subViews) this.subViews = [body];
			else this.subViews.push(body);
			return this._buildHome(init, itemType, body);
		}
	}

	abstract _buildHome(init: OSrcMainInit, itemType: ItemType, parentElt: Node): Promise<void>;

	viewShown: boolean;

	onViewShown() {
		this.viewShown = true;
		if (this.subViews) for (const v of this.subViews) VIEWS.onViewShown(v);
	}

	onViewHidden(closed: boolean) {
		this.viewShown = false;
		if (closed) {
			this.reg.env.place.closePlace();
			if (this.infoBroker) this.infoBroker.removeConsumer(this);
		}
		if (this.subViews) for (const v of this.subViews) VIEWS.onViewHidden(v, closed);
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (this.subViews) for (const v of this.subViews) {
			const r = visitor(v);
			if (r !== undefined) return r;
		}
	}

	async visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (this.subViews) for (const v of this.subViews) {
			const r = await visitor(v);
			if (r !== undefined) return r;
		}
	}

	onInfo(info: IInfo): void {}

	/** Construit une view de l'item (editor, dynGen , ...) */
	buildSrcView(v: IDatasSrcView): Promise<IView | null> {
		return v ? newSrcView(v, this.reg) : null;
	}

	async onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (msg.wspCd !== this.reg.env.wsp.code) return;
		const longDesc = this.reg.env.longDesc;
		if (SRC.srcRef(longDesc) === SRC.srcRef(msg) || (msg.type === EWspChangesEvts.perm && SRC.isSubUriOrEqual(msg.srcUri, longDesc.srcUri))) {
			const ldNew = await this.reg.env.wsp.fetchLongDesc(SRC.srcRef(longDesc), this);
			if (!this.isConnected) return;
			if (!this.wsp.isAvailable) await this.wsp.waitForAvailable(this);
			this.reg.env.longDesc = ldNew;
			this.shortDescs[0] = ldNew;
			//console.log("longDesc new:::", ldNew);
			this.reg.env.securityCtx = SEC.createSub(this.reg.env.securityCtx, ldNew.srcRoles, ldNew.srcRi, ldNew);
			this.tabs?.refresh(); //si perms sur tabs
			this.reg.env.longDescChange.emit(ldNew, longDesc);
		}
	}

	protected async focusNodeInEditor(ed: ItemXmlEd, info: InfoFocusNodeInItem) {
		await ed.initializedAsync;
		const wedMgr = ed.view.wedMgr;
		const docHolder = wedMgr.docHolder;
		if (!docHolder) return; //TODO si wedMgr.docHolderAsync uniquement.
		if (typeof info.xpath === "string") {
			const node = docHolder.house.findNodeByXpath(info.xpath);
			if (node) {
				if (wedMgr.drawn) {
					WEDLET.highlightWedletFromLink(XA.from(node), ed.view.wedMgr, 'nearest');
				} else {
					wedMgr.listeners.once("redrawAtEnd", () => {
						WEDLET.highlightWedletFromLink(XA.from(node), ed.view.wedMgr, 'nearest');
					});
				}
			}
		} else {
			if (wedMgr.drawn) {
				WEDLET.highlightWedletFromLink(info.xpath, ed.view.wedMgr, 'nearest');
			} else {
				wedMgr.listeners.once("redrawAtEnd", () => {
					WEDLET.highlightWedletFromLink(info.xpath as IXAddr, ed.view.wedMgr, 'nearest');
				});
			}
		}
	}

	//*** Apis IShortDescCtx ***
	shortDescs: JSrcFields[] = [];

	get emitter() {return this;}
}

REG.reg.registerSkin('wsp-itemmain', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		background-color: var(--bgcolor);
		color: var(--color);
	}

	[hidden] {
		display: none !important;
	}

	#ribbons {
		display: flex;
		min-height: 2.8em;
		min-width: 0;
	}

	#noTabs {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		border-top: 1px solid var(--border-color);
	}

	c-tabs {
		flex: 1;
	}

	#mainTab {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	c-resizer[c-orient="row"] {
		width: 1px;
	}

	c-resizer[c-orient="column"] {
		height: 1px;
	}

	#ribbons > c-resizer {
		width: 0;
	}

	#ribbons > [hidden] + c-resizer {
		display: none;
	}
`);

/** Area représentation la home de l'itemMain. */
class ItemHomeArea extends Area<SrcMain> {

	constructor(public init: OSrcMainInit) {
		super('');
	}

	needAsync(ctx: SrcMain): boolean {return true}

	async loadBody(ctx: SrcMain, lastDatas?: JLastDatas): Promise<HTMLElement> {
		const mainTab = <c-views-ctn id="mainTab"/>;
		const init = Object.create(this.init) as OSrcMainInit;
		init.lastDatas = lastDatas;
		await ctx._buildHome(init, ctx.reg.env.itemType, mainTab);
		return mainTab;
	}
}

/** Area de construction des vues secondaires de l'itemMain. */
class ItemSubTabArea extends SimpleTagArea<SrcMain, IView> {
	datas: IDatasItemTab;
	itemMain: SrcMain;

	constructor(datas: IDatasItemTab, itemMain: SrcMain) {
		super(datas.view.tag, null, datas.code);
		this.datas = datas;
		this.itemMain = itemMain;
		this._label = datas.label;
		this._description = datas.description;
		this._icon = datas.icon;
		this._visPerms = datas.showPerms;
	}

	async loadLibs(ctx: SrcMain): Promise<void> {
		const v = this.datas.view;
		if (v.freeLib) await loadFreeLib(ctx.reg.env.itemType.resolvePath(v.freeLib), ctx.reg.env.wsp.wspMetaUi.reg);
		else if (standardSrcViewsLibs[v.tag]) await ctx.reg.env.resolver.importJs(standardSrcViewsLibs[v.tag]);
		return super.loadLibs(ctx);
	}

	needAsync(ctx: SrcMain): boolean {
		if (super.needAsync(ctx)) return true;
		const v = this.datas.view;
		const lib = v.freeLib || standardSrcViewsLibs[v.tag];
		return lib && !customElements.get(v.tag);
	}

	protected newElt(ctx: SrcMain, lastDatas?: JLastDatas): IView {
		const elt = super.newElt(ctx);
		const atts = this.datas.view.atts;
		if (atts) for (const att in atts) elt.setAttribute(att, atts[att]);
		if (elt instanceof BaseElement) {
			const init = BASIS.newInit(this.datas.view.init, this.itemMain.reg);
			init.lastDatas = lastDatas;
			elt.initialize(init);
		}
		return elt;
	}
}


export async function newSrcView(v: IDatasSrcView, reg: IReg<IItemUiEnv>): Promise<HTMLElement> {
	//if (v.showPerm && !reg.hasPerm(v.showPerm)) return null;
	let init = v.init;
	if (v.freeLib) {
		const module = await loadFreeLib(reg.env.itemType.resolvePath(v.freeLib), reg.env.wsp.wspMetaUi.reg);
		if (module.newFreeSrcViewInit) init = module.newFreeSrcViewInit(v, reg);
	}
	if (!customElements.get(v.tag)) {
		const lib = standardSrcViewsLibs[v.tag];
		if (lib) await reg.env.resolver.importJs(lib);
	}
	const view = document.createElement(v.tag) as HTMLElement;
	view.setAttribute("c-resizable", "");
	const atts = v.atts;
	if (atts) for (const att in atts) view.setAttribute(att, atts[att]);
	if (view instanceof BaseElement) {
		view.initialize(BASIS.newInit(init, reg));
		if (isEltInitableAsyncPending(view)) await view.initializedAsync;
	}
	return view;
}


export const standardSrcViewsLibs: Dict<string> = {
	//ATTENTION laisser ":back:" et PAS "/@back@/" pour résoudre le bon credentials "omit"/"include" si les dev-core sont sur un autre domaine.
	"wsp-item-img": ":back:wsp/views/item/itemImg.js",
	"wsp-item-video": ":back:wsp/views/item/itemVideo.js",
	"wsp-item-audio": ":back:wsp/views/item/itemAudio.js",
	"wsp-item-iframe": ":back:wsp/views/item/itemIframe.js",
	"wsp-item-pdf": ":back:wsp/views/item/itemPdf.js",
	"wsp-item-dyngen": ":back:wsp/views/item/itemDynGen.js",
	"wsp-item-remote": ":back:wsp/views/item/itemRemote.js",//TODOsc7 : supprimer
	"wsp-item-remote-res": ":back:wsp/views/item/itemRemoteRes.js",
	"wsp-item-folder-tree": ":back:wsp/views/item/itemFolderTree.js",
	"wsp-item-msg-ss": ":back:wsp/views/item/itemMsgSs.js",
	"wsp-item-txt": ":back:wsp/views/item/itemTxt.js",
	"wsp-item-code-ed": ":back:wsp/views/item/itemCodeEd.js",
	"wsp-item-histo": ":back:wsp/views/item/itemHisto.js",
	"wsp-item-download": ":back:wsp/views/item/itemDownload.js",
	"wsp-item-groupviews": ":back:wsp/views/item/itemGroupViews.js",
	//"wsp-item-xmled": ":back:wsp/views/item/itemXmlEd.js", toujours préchargé.
	"wsp-task-dyngen": ":back:wsp/views/task/taskDynGen.js",
	"wsp-task-ct-edit": ":back:wsp/views/task/taskContent.js",
	"wsp-task-ct-dyngen": ":back:wsp/views/task/taskContent.js",
	"wsp-task-layout": ":back:wsp/views/task/taskLayout.js",
	"wsp-task-field": ":back:wsp/views/task/taskFields.js",
	"wsp-task-field-input": ":back:wsp/views/task/taskFields.js",
	"wsp-task-field-lc": ":back:wsp/views/task/taskFields.js",
	"wsp-task-xmlfield": ":back:wsp/views/task/taskFields.js",
	"wsp-task-exec-trans": ":back:wsp/views/task/taskFields.js",
};


/**
 * ItemMain pour les itemTypes de la famille "xml".
 */
export class ItemMainXml extends SrcMain {

	mainXmlEd: ItemXmlEd;

	async _buildHome(init: OSrcMainInit, itemType: ItemType, parentElt: Node): Promise<void> {
		const mainView = itemType.getDataSrcMainView(init.areaContext);
		if (mainView && mainView.xmlEditor) {
			this.mainXmlEd = parentElt.appendChild(new ItemXmlEd().initialize({
				reg: this.reg,
				editorKey: mainView.xmlEditor,
				writePerms: "action.itemMainView#edit.content",
				lastDatas: init.lastDatas
			}));
			return this.mainXmlEd.initializedAsync as unknown as Promise<void>;
		}
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoFocusNodeInItem) {
			const desc = this.reg.env.longDesc;
			if (desc.srcUri === info.srcRef || desc.srcId === info.srcRef) {
				let ed = this.mainXmlEd;
				if (this.tabs) {
					const currView = this.tabs.selectedTab.view;
					if (currView instanceof ItemXmlEd) {
						ed = currView;
					} else {
						this.tabs.selectTab(this.tabs.getTabByCode(""));
					}
				}
				if (ed) this.focusNodeInEditor(ed, info);
			}
		}
	}
}

//REG.reg.registerSkin('wsp-itemmain-xml', 1, /* language=CSS */ `	`);
customElements.define('wsp-itemmain-xml', ItemMainXml);


/**
 * ItemMain pour les itemTypes de la famille "res".
 */
export class ItemMainRes extends SrcMain {

	metaXmlEd?: ItemXmlEd;

	protected async _buildContent(init: OSrcMainInit): Promise<void> {
		this.reg.addToList("actions:item:burger", "pasteStream", 1, new PasteContent(), 1);
		return super._buildContent(init);
	}

	async _buildHome(init: OSrcMainInit, itemType: ItemType, parentElt: Node): Promise<void> {
		const mainView = itemType.getDataSrcMainView(init.areaContext);
		const resView = await this.buildSrcView(mainView?.resView);
		if (resView) {
			resView.style.flex = "1";
			parentElt.appendChild(resView);
		}
		const editor = mainView?.xmlEditor;
		if (editor) {
			if (resView) parentElt.appendChild(<Resizer c-orient="column"/>);
			const ed = this.metaXmlEd = parentElt.appendChild(<ItemXmlEd id="metaEd" î={{
				reg: this.reg,
				editorKey: editor,
				writePerms: "action.itemMainView#edit.content",
				lastDatas: init.lastDatas
			} as OItemXmlEdInit} c-resizable="" style="display: flex;"/>) as ItemXmlEd; //pour contrer display:contents par défaut du ItemXmlEd, incompatible avec Resizer.
			await ed.initializedAsync;
			const house = ed.view.wedMgr.docHolder.house as IWspXmlHouse;
			const metasSrcFields = house.srcFields;
			if (metasSrcFields && metasSrcFields.srcSt < 0 && metasSrcFields.srcDt > 0) {
				//Méta en corbeille à restaurer
				ed.view.wedMgr.setReadOnly("inTrash", true);
				const meatBar = parentElt.insertBefore(<div id="metaInTrashBar">
					<span>Ces métadonnées sont actuellement en corbeille</span>
					<div>
						<Button î={{reg: this.reg, label: "Restaurer", uiContext: "dialog"} as OButtonInit} disabled={this.reg.hasPermission("restore.history", metasSrcFields.srcRoles, undefined, metasSrcFields.srcRi) ? undefined : ""}
										onclick={async (ev: MouseEvent) => {
											await WSP.srcRestore(this.wsp, this, [SRC.srcRef(metasSrcFields)]);
											ed.view.wedMgr.setReadOnly("inTrash", false);
											meatBar.remove();
										}}/>
						<Button î={{reg: this.reg, label: "Réinitialiser", uiContext: "dialog"} as OButtonInit}
										disabled={this.reg.hasPermission("action.itemMainView#edit.content", metasSrcFields.srcRoles, undefined, metasSrcFields.srcRi) ? undefined : ""}
										onclick={async (ev: MouseEvent) => {
											await saveDoc(this, house, DOM.newDomDoc(), house.itemType.getModel(), SRC.srcRef(metasSrcFields));
											ed.view.wedMgr.setReadOnly("inTrash", false);
											meatBar.remove();
										}}/>
					</div>
				</div>, ed);
			}
		}
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoFocusNodeInItem) {
			const desc = this.reg.env.longDesc;
			if (desc.srcUri === info.srcRef || desc.srcId === info.srcRef) {
				if (this.metaXmlEd) this.focusNodeInEditor(this.metaXmlEd, info);
			}
		}
	}

}

REG.reg.registerSkin('wsp-itemmain-res', 1, /* language=CSS */ `
	wsp-item-xmled {
		flex-direction: column;
		flex: 1;
		min-height: 7em;
	}

	#metaInTrashBar {
		border-bottom: 1px solid var(--border-color);
		text-align: center;
		padding: .5em;
		background-color: var(--alt1-bgcolor);
	}

	#metaInTrashBar > span {
		color: var(--warning-color);
		font-weight: bold;
	}

	#metaInTrashBar > div {
		display: flex;
		justify-content: center;
	}
`);
customElements.define('wsp-itemmain-res', ItemMainRes);


/**
 * ItemMain pour les itemTypes de la famille "folder".
 */
export class ItemMainFolder extends SrcMain {

	async _buildHome(init: OSrcMainInit, itemType: ItemType, parentElt: Node): Promise<void> {
		const msgSs = await this.buildSrcView({tag: "wsp-item-msg-ss"});
		parentElt.appendChild(msgSs);

		const ctnH = parentElt.appendChild(<div id="ctnH"/>);

		//resTree. TODO passer en view configurable : mainView.treeView ?
		const treeView = await this.buildSrcView({tag: "wsp-item-folder-tree"});
		ctnH.appendChild(treeView);

		//resView et editor de metas  TODO à rendre dynamique en fonction de la sélection dans le folder-tree.
		// const mainView = itemType.getDataSrcMainView(init.areaContext);
		// const resView = await this.buildSrcView(mainView && mainView.resView);
		// const editor = mainView && mainView.xmlEditor ? mainView.xmlEditor : itemType.datas.editors ? Object.keys(itemType.datas.editors)[0] : null;
		// if (resView && editor) {
		// 	ctnH.appendChild(<Resizer c-orient="row"/>);
		// 	const ctnV = ctnH.appendChild(<div id="ctnV" c-resizable=""/>);
		// 	ctnV.appendChild(resView);
		// 	ctnV.appendChild(<Resizer c-orient="column"/>);
		// 	const ed = ctnV.appendChild(<ItemXmlEd î={{
		// 		reg: this.reg,
		// 		editorKey: editor,
		// 		writePerms: "action.itemMainView#edit.content",
		// 		lastDatas: init.lastDatas
		// 	} as OItemXmlEdInit} c-resizable="" style="display: flex;"/>); //pour contrer display:contents par défaut du ItemXmlEd, incompatible avec Resizer.
		// } else if (resView) {
		// 	ctnH.appendChild(<Resizer c-orient="row"/>);
		// 	ctnH.appendChild(resView);
		// } else if (editor) {
		// 	ctnH.appendChild(<Resizer c-orient="row"/>);
		// 	const ed = ctnH.appendChild(<ItemXmlEd î={{
		// 		reg: this.reg,
		// 		editorKey: editor,
		// 		writePerms: "action.itemMainView#edit.content",
		// 		lastDatas: init.lastDatas
		// 	} as OItemXmlEdInit} c-resizable="" style="display: flex;"/>); //pour contrer display:contents par défaut du ItemXmlEd, incompatible avec Resizer.
		// }
	}
}

REG.reg.registerSkin('wsp-itemmain-folder', 1, /* language=CSS */ `
	#ctnH {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	#ctnV {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	wsp-item-xmled {
		flex-direction: column;
		flex: 1;
	}
`);

customElements.define('wsp-itemmain-folder', ItemMainFolder);


export interface OTaskMainInit extends OSrcMainInit {
	viewOptions?: any
	lastDatas?: JTaskStates
}


/**
 * ItemMain pour les itemTypes de la famille "task".
 */
export class TaskMain extends SrcMain {

	reg: IReg<ITaskUiEnv>;

	protected startRedraw: Element;
	protected config: OTaskMainInit;

	async redrawMainView(): Promise<void> {
		//cleanup
		this.resetMainView();
		//reconstruction
		await this._buildHome(this.config, this.reg.env.itemType, this.shadowRoot);
		if (this.viewShown) for (const v of this.subViews) VIEWS.onViewShown(v);
	}

	/** Utilisé après enregistrement d'une tâche en création pour permettre son remplacement par la tache créée*/
	resetMainView() {
		for (const v of this.subViews) VIEWS.onViewHidden(v, true);
		while (this.startRedraw.nextElementSibling) this.startRedraw.nextElementSibling.remove();
		this.subViews.length = 0;
		this.reg.env.initStates = null; // plus d'init avec un état issu des lastDatas.
		this.setNeedValid(false);
		(this.reg.env.taskEditBroker as InfoBrokerBasic).removeAllConsumers();
	}

	setNeedValid(needValid: boolean) {
		if (this._needValid !== needValid) {
			this._needValid = needValid;
			if (this.isConnected) this.dispatchEvent(new CustomEvent("wsp-need-valid", {bubbles: true, detail: needValid}));
		}
	}

	protected _needValid: boolean;

	connectedCallback() {
		super.connectedCallback();
		if (this._needValid) this.dispatchEvent(new CustomEvent("wsp-need-valid", {bubbles: true, detail: true}));
	}

	//Pas de tabs secondaires sur les tasks.
	protected async _buildContent(init: OTaskMainInit): Promise<void> {
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:page", this.shadowRoot);
		this._initAndInstallSkin(this.localName, init);
		this.config = init;
		this.startRedraw = this.shadowRoot.lastElementChild;
		this.reg.env.longDescChange.add(async () => {
			//On récupère le contenu
			await this.fetchContent();
			return this.redrawMainView();
		});
		//console.log("_buildContent init.lastDatas:::", init.lastDatas);
		if (init.lastDatas && (init.lastDatas as JLDTaskMain).tkSt) this.reg.env.initStates = (init.lastDatas as JLDTaskMain).tkSt;
		if (!SRC.isNewSrcUri(this.reg.env.longDesc.srcUri)) {
			await this.fetchContent();
		}
		return this._buildHome(init, this.reg.env.itemType, this.shadowRoot);
	}

	private async fetchContent() {
		if (!this.reg.env.taskContent) this.reg.env.taskContent = DOM.parseDomValid(await WSP.fetchStreamText(this.wsp, this.reg.env.uiRoot, SRC.srcRef(this.reg.env.longDesc)));
	}

	async _buildHome(init: OTaskMainInit, itemType: ItemTypeTask, parentElt: Node) {
		const dataViews = (itemType.datas as IDatasTaskItemType).taskViews;
		const longDesc = this.reg.env.longDesc;
		this.reg.env.taskEditBroker = new InfoBrokerBasic();
		let userNames: string[];
		let view: IDatasTaskView;
		findView: for (const v of dataViews) {
			//Restriction sur l'état de la tâche
			if (v.ifLifeCycleState && !v.ifLifeCycleState.test(longDesc.lcSt)) continue findView;
			//Perm
			if (v.ifPerm && !this.reg.hasPerm(v.ifPerm)) continue findView;
			//Restictions sur l'implication du user dans la tâche
			if (v.ifInvolvement > 0) {
				if (!longDesc.rspUsrs) continue findView;
				if (!userNames) userNames = await this.reg.env.universe.auth.fetchFlattenedGroups();
				let found: boolean;
				for (const r of longDesc.rspUsrs) {
					if (userNames.indexOf(r.usr) >= 0 && (TASK.findInvolvementForResps(itemType, r.resp, longDesc) & v.ifInvolvement) > 0) {
						found = true;
						break;
					}
				}
				if (!found) continue findView; //Involvement pas trouvé
			}
			//Restictions sur l'app courante
			if (v.ifInApp) {
				const app = DOMSH.findLogicalFlatParentOrSelf(this.reg.env.uiRoot, null, isApp);
				if (!app || !v.ifInApp.test(app.localName)) continue findView; //App pas trouvé
			}
			//Restriction Custom
			if (v.customSelector && !v.customSelector(this.reg, init.viewOptions)) continue findView;
			//Toutes les restrictions sont validées, on sélectionne cette view
			view = v;
			break findView;
		}
		if (!view) throw Error("No view found for task: " + longDesc.srcUri);
		//console.log("view::::", view, longDesc);
		const widget = await this.buildSrcView(view.viewDef);
		if (!this.subViews) this.subViews = [widget];
		else this.subViews.push(widget);
		parentElt.appendChild(widget);
		if (this.reg.env.initStates) {
			this.reg.env.taskEditBroker.dispatchInfo(new InfoChangeTask({reg: this.reg, fieldName: 'init'}), this);
		}
	}

	async onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (msg.type === EWspChangesEvts.lcSt) return; //un autre event de type update suis derrière... XXX suppr ce double event coté serveur ?
		const longDesc = this.reg.env.longDesc;
		if (SRC.srcRef(longDesc) === SRC.srcRef(msg)) {
			const ldNew = await this.reg.env.wsp.fetchLongDesc(SRC.srcRef(msg), this);
			if (!this.isConnected) return;
			this.reg.env.longDesc = ldNew;
			this.reg.env.taskContent = null;
			this.shortDescs[0] = ldNew;
			this.reg.env.longDescChange.emit(ldNew, longDesc);
		}
	}


	onViewBeforeHide(close?: boolean): boolean {
		if (close) {
			const editBroker = this.reg.env.taskEditBroker;
			if (editBroker) {
				const st = new InfoReqStateTask();
				editBroker.dispatchInfo(st, this);
				return !st.isDirty;
			}
		}
		return true;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		if (close) {
			const editBroker = this.reg.env.taskEditBroker;
			if (editBroker) {
				const st = new InfoReqStateTask();
				editBroker.dispatchInfo(st, this);
				if (st.isDirty) {
					if (SRC.isNewSrcUri(this.reg.env.longDesc.srcUri)) {
						return await POPUP.confirm("Voulez-vous vraiment abandonner cette tâche en création ?", this, {okLbl: "Abandonner la création"});
					} else {
						return await POPUP.confirm("Cette tâche est en cours d'édition, voulez-vous vraiment abandonner vos modifications ?", this, {okLbl: "Abandonner les modifications"});
					}
				}
				return true;
			}
		}
		return true;
	}


	buildLastDatas?(parentLastDatas: JLDTaskMain): void {
		const editBroker = this.reg.env.taskEditBroker;
		if (editBroker) {
			const st = new InfoReqStateTask();
			st.states = {};
			editBroker.dispatchInfo(st, this);
			if (st.isDirty) parentLastDatas.tkSt = st.states;
		}
		//console.log("TaskMain.buildLastDatas:::", JSON.stringify(parentLastDatas));
	}

}

export interface JLDTaskMain extends JLastDatas {
	tkSt: JTaskStates
}

REG.reg.registerSkin('wsp-taskmain', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}
`);

customElements.define('wsp-taskmain', TaskMain);


/** Stylage des SubTabs dans l'itemMain. */
REG.reg.registerSkin('itemMain/c-tabs', 1, /* language=CSS */ `
	[hidden] {
		display: none !important;
	}

	#tabs {
		justify-content: center;
		padding-top: .5em;
	}

	c-tab {
		font-size: var(--label-size);
	}
`);

/** Stylage de chaque Tab des SubTabs dans l'itemMain. */
REG.reg.registerSkin('itemMain/c-tab', 1, /* language=CSS */ `
	.label {
		flex: 0 1 auto;
	}
`);

/** Interface commune aux containers des ItemMain. */
export interface IItemViewer extends IViewApi, IInfoConsumer, IRegPointer<IWspUiEnv> {

	/**
	 * Initialisation de l'itemViewer.
	 * Ne doit être appelé qu'une fois !
	 *
	 * @param select srcRef de l'item à sélectionner issu des JWspAppDef (queryString de l'URL). Surcharge les lastDatas.
	 */
	initViewer(reg: IReg<IWspUiEnv>, mainViewParent: HTMLElement, lastDatas: JLastDatas, select: srcRef): Promise<any>;

	/**
	 * Ouvre une source.
	 * @param silent si false/null et si fallbackOpenSrcRef est null, une notif. sera affichée au user en cas d'erreur (srcRef non trouvé...)
	 * @return true: ouvert, false: abandon user, null: srcRef non ouvrable.
	 */
	openSrcRef(srcRef: srcRef, silent?: boolean): Promise<boolean | null>

	/**
	 *
	 * @param silent si false/null, une notif. sera affichée au user en cas d'erreur (srcRef non trouvé...)
	 * @return true: ouvert, null: srcRef pas trouvé.
	 */
	fallbackOpenSrcRef?: (srcRef: srcRef, longdesc: JSrcFields, silent?: boolean) => Promise<true | null>
}

class ItemMainRename extends RenameSrc<SrcMain> {

	async execute(ctx: SrcMain, ev?: Event): Promise<srcUri[]> {
		const newUris = await super.execute(ctx, ev);
		if (newUris && newUris.length > 0 && ctx.infoBroker) {
			//Pas de possibilité actuellement coté java d'exclure notre clId de la remontée des wspChanges events sur un rename.
			//Donc pas d'autre solution qu'un timeout pour re-sélectionner la cible du renommage.
			setTimeout(() => {
				ctx.infoBroker.dispatchInfo(new InfoFocusItem(newUris[0]), ctx);
			}, 300);
		}
		return newUris;
	}
}

class ItemMainMove extends MoveToSrc<SrcMain> {

	async execute(ctx: SrcMain, ev?: Event): Promise<srcUri[]> {
		const newUris = await super.execute(ctx, ev);
		if (newUris && ctx.infoBroker) {
			//Pas de possibilité actuellement coté java d'exclure notre clId de la remontée des wspChanges events sur un moveTo.
			//Donc pas d'autre solution qu'un timeout pour re-sélectionner la cible du renommage.
			setTimeout(() => {
				ctx.infoBroker.dispatchInfo(new InfoFocusItem(newUris[0]), ctx);
			}, 300);
		}
		return newUris;
	}
}


class ItemMainConverters extends ActionMenu<SrcMain> {
	constructor(converters: IAction<SrcMain>[]) {
		super("converters");
		this._label = "Convertir...";
		this._group = "edit";
		this._actions = converters;
		this._visPerms = "write.node.convert";
	}
}

export async function convertBySwitchTag(ctx: IRegPointer<IItemUiEnv>, props: { tagName: string, tagNs: string, cleanupSchemaItModel?: string }) {
	const env = ctx.reg.env;
	const house = await env.place.getHouse(env.wsp, SRC.srcRef(env.longDesc));
	if (house && isHouseUpdatable(ctx, house)) {
		const doc = house.getDocumentForSave();
		const oldElt = doc.documentElement.firstElementChild;
		const newElt = doc.createElementNS(props.tagNs, props.tagName);
		while (oldElt.hasChildNodes()) newElt.appendChild(oldElt.firstChild);
		oldElt.replaceWith(newElt);
		return saveDoc(ctx, house, doc, props.cleanupSchemaItModel);
	}
}

export async function convertByXsl(ctx: IRegPointer<IItemUiEnv>, props: { xslPath?: string, xsl: string | IEndPoint | XSLTProcessor, cleanupSchemaItModel?: string, xslParams: { name: string, value: string }[] }) {
	const env = ctx.reg.env;
	if (!(props.xsl instanceof XSLTProcessor)) {
		let dom: Document;
		if (typeof props.xsl === 'object') {
			dom = await props.xsl.fetchDom();
		} else if (typeof props.xsl === 'string') {
			dom = DOM.parseDom(props.xsl, env.resolver)
		} else if (props.xslPath)
			await ctx.reg.env.itemType.resolvePath(props.xslPath).fetchDom();

		if (!dom)
			throw "xsl not available";

		if (dom.documentElement.firstElementChild.nodeName === "xsl:output") {
			// Les noeuds texte peuvent perturber la normalisation wed du flux XML transformé
			// Cas problématique : set d'#element avec des #text entre deux => seul le premier est préservé
			DOM.setAttr(dom.documentElement.firstElementChild, "indent", "no");
		}

		props.xsl = new XSLTProcessor();
		props.xsl.importStylesheet(dom);
	}
	if (props.xsl instanceof XSLTProcessor)
		props.xslParams?.forEach((entry) => (props.xsl as XSLTProcessor).setParameter("", entry.name, entry.value));
	const house = await env.place.getHouse(env.wsp, SRC.srcRef(env.longDesc));
	if (house && isHouseUpdatable(ctx, house)) {
		if (props.cleanupSchemaItModel) {
			const itemType = env.wsp.wspMetaUi.getItemType(props.cleanupSchemaItModel);
			if (itemType) props.xsl.setParameter("", "targetTagName", itemType.getTagRoot());
		}
		return saveDoc(ctx, house, DOM.cleanupDom(props.xsl.transformToDocument(house.getDocumentForSave()), true, false, false), props.cleanupSchemaItModel);
	}
}

function isHouseUpdatable(ctx: IRegPointer<IItemUiEnv>, house: IWspXmlHouse) {
	if (house.lockedBy) {
		POPUP.showNotifForbidden("Cet item est en cours d'édition, conversion impossible.", ctx.reg.env.uiRoot);
		return false;
	}
	return true;
}

async function saveDoc(ctx: IRegPointer<IItemUiEnv>, house: IWspXmlHouse, doc: Document, cleanupSchemaItModel?: string, resRef?: srcRef) {
	const wasDirty = house.isDirty;
	try {
		house.setDirty(false); //bloque tout save
		const env = ctx.reg.env;
		if (cleanupSchemaItModel) {
			const targetSchema = await env.wsp.wspMetaUi.getItemType(cleanupSchemaItModel)?.getSchema();
			if (targetSchema) {
				targetSchema.bindToDom(doc).correctDocument({autoMutate: true, autoComplete: true, autoCleanup: true, autoNormXml: true, autoNormChars: true})
				targetSchema.fixDocForExport(doc);
			}
		}
		const qs = IO.qs("cdaction", "PutSrc", "param", env.wsp.code, "refUri", resRef || SRC.srcRef(env.longDesc));
		await env.wsp.wspServer.config.wspSrcUrl.fetchVoid(qs, {method: "PUT", body: DOM.ser(doc, true)});
	} finally {
		if (wasDirty) house.setDirty(true);
	}
}


function addToBurger(action: IAction<SrcMain>, sortKey?: number) {
	REG.reg.addToList("actions:item:burger", action.getId(), 1, action, sortKey);
}

addToBurger(new ItemMainRename(), 10);
addToBurger(new ItemMainMove(), 20);
addToBurger(new ReloadSrc());
addToBurger(new ExportScar(), 1010);// Après les import/export des resources.

//kill l'action de focus dans le menu hamburger de l'itemMain
REG.reg.addToList("actions:item:burger", "focusItem", 1, null);

// Toolbar pour les flux binaires dans les itemImg, itemIframe, itemPdf, ...
REG.reg.addToList("actions:wsp:binaryStream", "downloadSrc", 1, ExportRes.SINGLETON());


if (Desk.electron) {
	const openWidthLOAction = new Action<IRegPointer<IItemUiEnv>>()
		.setLabel("Ouvrir avec LibreOffice")
		.setVisible((ctx) => {
			const {env} = ctx.reg;
			return env.itemType.matchType("application/vnd.oasis.opendocument.text") === 100
				|| env.itemType.matchType("application/vnd.oasis.opendocument.spreadsheet") === 100
				|| env.itemType.matchType("application/vnd.oasis.opendocument.graphics") === 100
				|| env.itemType.matchType("application/vnd.oasis.opendocument.presentation") === 100
				|| env.itemType.matchType("application/vnd.oasis.opendocument.formula") === 100;
		})
		.setExecute(async (ctx) => {
			const {env} = ctx.reg;
			const url = `${env.wsp.wspServer.config.wspDavUrl}${env.wsp.code}${env.itemType.getMainStreamSrcUri(env.longDesc)}`.replace("/web/", "/api/");
			const openReply = await IO.sendMessage({type: "client:modules:odEditor:open", url: encodeURI(url)});
			if (openReply.type == 'error') ERROR.log(openReply.msg);
			else if (openReply.type === "notFound") POPUP.showNotifInfo("L'éditeur de document 'OpenDocument' (LibreOffice, ...) n'a pu être trouvé.", env.uiRoot);
		});

	addToBurger(openWidthLOAction);
}

/**
 * Ajoute les listeners pour autoriser le drag et le drop d'un fichier sur une srcView
 *
 * Remarque : le plugin PDF natif de Chrome intercepte les évènements de drag
 *
 * @param reg
 * @param srcView Le widget à augmenté avec le drop, doit avoir un shadow attaché
 * @param callback Fonction a appelé sur le drop, remplace le flux principal si non précisé
 *
 */
export function installSrcViewFileDrop(reg: IReg<IItemUiEnv>, srcView: BaseElement, callback?: (entry: DataTransferItem) => void) {
	reg.installSkin("srcview/filedrop", srcView.shadowRoot);

	const {env} = reg;

	const dropLayer = <div class="fileDropLayer">Importez le fichier en le déposant ici.</div>;

	srcView.addEventListener('dragenter', function (ev: DragEvent) {
		const item = ev.dataTransfer.items[0];
		if (item && item.kind == 'file' && env.itemType.matchType(item.type) > 1) {
			this.shadowRoot.appendChild(dropLayer);
		}
	});

	srcView.addEventListener('dragover', function (ev: DragEvent) {
		if (dropLayer.isConnected) {
			ev.preventDefault();
			ev.stopPropagation();
		}
	});

	srcView.addEventListener('dragleave', function () {
		if (dropLayer.isConnected) this.shadowRoot.removeChild(dropLayer);
	});

	srcView.addEventListener('drop', async function (ev: DragEvent) {
		this.shadowRoot.removeChild(dropLayer);
		const item = ev.dataTransfer.items[0];
		if (callback) {
			callback(item);
		} else {
			const progress = POPUP.showProgress(srcView, "Import en cours...");
			try {
				await ITEM.update(env.wsp, env.uiRoot, SRC.srcRef(env.longDesc), item.getAsFile(), progress.progressCallback);
				progress.close();
			} catch (e) {
				progress.close();
				await ERROR.report("Une erreur est survenue lors de l'import de la ressource.", e, reg);
			}
		}
	});
}


REG.reg.registerSkin('srcview/filedrop', 1, /* language=CSS */ `
	:host {
		position: relative;
	}

	.fileDropLayer {
		position: absolute;
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 2em;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		background-color: rgba(255, 255, 255, 0.6);
	}
`);

